import { Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { AssetSearchDialogComponent } from 'src/app/dialogs/asset-search-dialog/asset-search-dialog.component';

@Component({
	selector: 'app-user-favourites',
	templateUrl: './user-favourites.component.html',
	styleUrls: ['./user-favourites.component.scss']
})
export class UserFavouritesComponent implements OnDestroy {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.AccountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	public FavouriteAssets: LevelAsset[] = [];

	public FilteredFavouriteAssets: LevelAsset[] = [];

	public AccountGUID: string;

	public OrganisationUID: string;

	public currentUser: WebAppUser;

	public fields: { text: string, value: string } = { text: 'Name', value: 'AssetID' };

	public hierachy: OrganisationsHierarchy[];

	public selectedLocation?: OrganisationsHierarchy;

	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	/** locationDropdown */
	@ViewChild('dropdown') locationDropdown: DropDownList;

	constructor(
		public assetsDataService: AssetsDataService,
		public globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private siteSubscriptions: siteSubscriptions,
		private router: Router,
		public dialog: MatDialog
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, account, selectedOrganisationUID, hierarchicalData, currentUser]) =>
				!!selectedAccountGUID && !!account && !!selectedOrganisationUID && !!hierarchicalData && !!currentUser)
		).subscribe({
			next: ([selectedAccountGUID, account, selectedOrganisationUID, hierarchicalData, currentUser]) => {
				this.AccountGUID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.hierachy = JSON.parse(hierarchicalData);

				this.MainLocations = [];
				if (this.hierachy[0].SubChild) {
					this.hierachy[0].SubChild.forEach(subchild => {
						this.MainLocations.push(subchild);
					});
				}

				this.getFavouriteAssets();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getFavouriteAssets
	 */
	getFavouriteAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getUserFavouriteAssets$(this.AccountGUID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				if (result[0].Data) {
					this.FavouriteAssets = JSON.parse(result[0].Data);
					this.FilteredFavouriteAssets = this.FavouriteAssets;
				} else {
					this.FavouriteAssets = [];
				}
			},
			error: (error) => {
				if (error.status == 404) {
					this.FavouriteAssets = [];
				} else {
					this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getUserFavouriteAssets$", false);
				}
			},
		});
	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * goToAsset
	 */
	goToAsset(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`./${this.AccountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}

	/**
	 * removeFavourite
	 * @param asset asset
	 */
	removeFavourite(asset: LevelAsset) {
		this.globalFunctions.UserConfirm$("Remove " + asset.Name + " - " + asset.Description + " as a favourite?").subscribe(async OKResult => {
			if (OKResult) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				this.assetsDataService.deleteUserFavouriteAssets$(this.AccountGUID, this.OrganisationUID, asset.AssetID, this.currentUser.Sub).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (result) => {
						this.getFavouriteAssets();
						this.globalFunctions.DisplaySnackBar(
							{
								message: "Favourite Removed",
								duration: 1000
							}
						);
					},
					error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.deleteUserFavouriteAssets$", false); },
				});
			}
		});
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterFavouriteAssets();
		this.FilteredFavouriteAssets = this.FilteredFavouriteAssets.filter(data => {
			const name = `${data.Name}`.toLowerCase();
			const description = `${data.Description}`.toLowerCase() || '';

			const combinedStr = `${name} ${description}`;
			return combinedStr.includes(query);
		});
	}

	/**
	 * filterByLocation
	 * @param event event
	 * @param cancel cancel
	 */
	filterByLocation(event: any, cancel?: boolean) {
		if (cancel) {
			this.selectedLocation = undefined;
			this.locationDropdown.clear();
		} else if (event.itemData) {
			this.selectedLocation = <OrganisationsHierarchy>event.itemData;
		}
		this.filterFavouriteAssets();
	}

	/**
	 * filterFavouriteAssets
	 */
	filterFavouriteAssets() {
		if (this.selectedLocation) {
			let locationAssets: LevelAsset[] = this.flattenAssets([this.selectedLocation]);
			let locationAssetIDs: number[] = [];
			locationAssets.forEach(asset => {
				locationAssetIDs.push(asset.AssetID);
			});
			this.FilteredFavouriteAssets = this.FavouriteAssets.filter((x: LevelAsset) => locationAssetIDs.includes(x.AssetID));
		} else {
			this.FilteredFavouriteAssets = this.FavouriteAssets;
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * searchForAsset
	 */
	searchForAsset() {
		let assetList: LevelAsset[] = this.flattenAssets(this.hierachy);
		const dialogRef = this.dialog.open(AssetSearchDialogComponent, {
			data: {
				assetList: assetList,
				hierarchy: this.hierachy
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let asset = assetList.find((x: LevelAsset) => x.AssetID == result.asset);
			if (asset) {
				this.addFavourite(asset);
			}
		});
	}

	/**
	 * addFavourite
	 * @param asset asset
	 */
	addFavourite(asset: LevelAsset) {
		this.globalFunctions.UserConfirm$("Add " + asset.Name + " - " + asset.Description + " as a favourite?").subscribe(async OKResult => {
			if (OKResult) {
				// Create a local Subject to control the subscription lifecycle
				const fetchDataSubject = new Subject<void>();

				this.assetsDataService.postUserFavouriteAssets$(this.AccountGUID, this.OrganisationUID, asset.AssetID).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (result) => {
						this.getFavouriteAssets();
						this.globalFunctions.DisplaySnackBar(
							{
								message: "Favourite Added",
								duration: 1000
							}
						);
					},
					error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postUserFavouriteAssets$", false); },
				});
			}
		});
	}
}
